import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  InputBase,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { FilterFieldDef } from './AddFilterModal.types';
import { AdvancedFilter } from './FilterBar.props';
import { RELATIVE_DATE_RANGES } from '../../../constants/attendanceReport';

export interface AddFilterConfigureProps {
  field: FilterFieldDef;
  onApply: (filter: AdvancedFilter) => void;
  onBack: () => void;
}

interface RelativeDateOption {
  label: string;
  value: string;
}

export const AddFilterConfigure: React.FC<AddFilterConfigureProps> = ({
  field,
  onApply,
  onBack,
}) => {
  const theme = useTheme();
  const [operator, setOperator] = useState(field.operators[0]?.value ?? '');
  const [value, setValue] = useState('');
  const [value2, setValue2] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateMode, setDateMode] = useState<'relative' | 'absolute'>('relative');
  const [relativeDateFrom, setRelativeDateFrom] = useState<RelativeDateOption | null>(null);
  const [relativeDateTo, setRelativeDateTo] = useState<RelativeDateOption | null>(null);
  const [boolValue, setBoolValue] = useState('true');
  const [listValues, setListValues] = useState<string[]>([]);

  const relativeOptions = useMemo(() => RELATIVE_DATE_RANGES as RelativeDateOption[], []);

  const inputSx = {
    border: `1px solid ${theme.palette.v4.filterInputBorder}`,
    borderRadius: 1,
    px: 1.5,
    py: 0.5,
    fontSize: '0.875rem',
  };

  const getDateValue = (pos: 'from' | 'to'): string => {
    if (dateMode === 'relative') {
      return pos === 'from'
        ? (relativeDateFrom?.value ?? '')
        : (relativeDateTo?.value ?? '');
    }
    return pos === 'from' ? dateFrom : dateTo;
  };

  const getDateDisplay = (pos: 'from' | 'to'): string => {
    if (dateMode === 'relative') {
      return pos === 'from'
        ? (relativeDateFrom?.label ?? '')
        : (relativeDateTo?.label ?? '');
    }
    return pos === 'from' ? dateFrom : dateTo;
  };

  const handleApply = () => {
    let display = '';
    let finalValue = value;

    if (field.type === 'boolean') {
      finalValue = boolValue;
      display = boolValue === 'true' ? 'Yes' : 'No';
    } else if (field.type === 'list') {
      finalValue = listValues.join(',');
      display = `${operator === 'isAnyOf' ? 'Is any of' : 'Is not'}: ${listValues.join(', ')}`;
    } else if (field.type === 'date') {
      const fromVal = getDateValue('from');
      const fromDisplay = getDateDisplay('from');
      const toVal = getDateValue('to');
      const toDisplay = getDateDisplay('to');

      if (operator === 'between') {
        display = `${fromDisplay} to ${toDisplay}`;
      } else if (operator === 'isEmpty' || operator === 'isNotEmpty') {
        display = operator === 'isEmpty' ? 'Is empty' : 'Is not empty';
      } else {
        const opLabel = field.operators.find((o) => o.value === operator)?.label ?? operator;
        display = `${opLabel}: ${fromDisplay}`;
      }

      onApply({
        field: field.name,
        dataKey: field.key,
        type: field.type,
        operator,
        value: fromVal,
        value2: operator === 'between' ? toVal : undefined,
        dateFrom: fromVal,
        dateTo: operator === 'between' ? toVal : undefined,
        display,
      });
      return;
    } else if (operator === 'between') {
      display = `${value} to ${value2}`;
    } else {
      const opLabel = field.operators.find((o) => o.value === operator)?.label ?? operator;
      display = `${opLabel}: ${value}`;
    }

    onApply({
      field: field.name,
      dataKey: field.key,
      type: field.type,
      operator,
      value: finalValue,
      value2: operator === 'between' ? value2 : undefined,
      display,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleApply();
  };

  const radioSx = { '&.Mui-checked': { color: theme.palette.v4.accent } };

  const renderOperatorSelect = () => (
    <Select value={operator} onChange={(e) => setOperator(e.target.value)} size="small" fullWidth sx={{ mb: 1.5 }}>
      {field.operators.map((op) => (
        <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
      ))}
    </Select>
  );

  const renderRelativeDatePicker = (
    label: string,
    selected: RelativeDateOption | null,
    onChange: (val: RelativeDateOption | null) => void,
    ariaLabel: string,
  ) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>{label}</Typography>
      <Autocomplete
        size="small"
        options={relativeOptions}
        value={selected}
        onChange={(_, val) => onChange(val)}
        getOptionLabel={(opt) => opt.label}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="e.g. first day of this month"
            inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
          />
        )}
        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
      />
    </Box>
  );

  const renderAbsoluteDatePicker = (
    label: string,
    val: string,
    onChange: (val: string) => void,
    ariaLabel: string,
  ) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>{label}</Typography>
      <InputBase
        type="date"
        value={val}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        inputProps={{ 'aria-label': ariaLabel }}
        sx={inputSx}
      />
    </Box>
  );

  const showDateInputs = operator !== 'isEmpty' && operator !== 'isNotEmpty';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }} onKeyDown={handleKeyDown}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          sx={{ textTransform: 'none', color: theme.palette.text.secondary, mb: 1 }}
        >
          Back
        </Button>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {field.name}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Type: {field.type}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1 }}>
        {field.type === 'boolean' && (
          <RadioGroup value={boolValue} onChange={(e) => setBoolValue(e.target.value)}>
            <FormControlLabel value="true" control={<Radio size="small" sx={radioSx} />} label="Yes" />
            <FormControlLabel value="false" control={<Radio size="small" sx={radioSx} />} label="No" />
          </RadioGroup>
        )}

        {field.type === 'list' && (
          <>
            {renderOperatorSelect()}
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 1 }}>
              Enter comma-separated values below
            </Typography>
            <InputBase
              placeholder="Value1, Value2..."
              value={listValues.join(', ')}
              onChange={(e) => setListValues(e.target.value.split(',').map((v) => v.trim()).filter(Boolean))}
              fullWidth
              inputProps={{ 'aria-label': `${field.name} values` }}
              sx={inputSx}
            />
          </>
        )}

        {field.type === 'text' && (
          <>
            {renderOperatorSelect()}
            {operator !== 'isEmpty' && (
              <InputBase placeholder="Enter value..." value={value} onChange={(e) => setValue(e.target.value)}
                fullWidth inputProps={{ 'aria-label': `${field.name} value` }} sx={inputSx} />
            )}
          </>
        )}

        {field.type === 'number' && (
          <>
            {renderOperatorSelect()}
            {operator !== 'isEmpty' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <InputBase placeholder={operator === 'between' ? 'From' : 'Value'} type="number" value={value}
                  onChange={(e) => setValue(e.target.value)} fullWidth
                  inputProps={{ 'aria-label': `${field.name} value` }} sx={inputSx} />
                {operator === 'between' && (
                  <InputBase placeholder="To" type="number" value={value2} onChange={(e) => setValue2(e.target.value)}
                    fullWidth inputProps={{ 'aria-label': `${field.name} value 2` }} sx={inputSx} />
                )}
              </Box>
            )}
          </>
        )}

        {field.type === 'date' && (
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
            {field.operators.length > 1 && renderOperatorSelect()}

            {showDateInputs && (
              <>
                <ToggleButtonGroup
                  value={dateMode}
                  exclusive
                  onChange={(_, val) => val && setDateMode(val)}
                  size="small"
                  sx={{ mb: 1 }}
                >
                  <ToggleButton
                    value="relative"
                    sx={{
                      textTransform: 'none', fontSize: '12px', px: 2,
                      '&.Mui-selected': { bgcolor: theme.palette.v4.accent, color: 'common.white', '&:hover': { bgcolor: theme.palette.v4.accent } },
                    }}
                  >
                    Relative
                  </ToggleButton>
                  <ToggleButton
                    value="absolute"
                    sx={{
                      textTransform: 'none', fontSize: '12px', px: 2,
                      '&.Mui-selected': { bgcolor: theme.palette.v4.accent, color: 'common.white', '&:hover': { bgcolor: theme.palette.v4.accent } },
                    }}
                  >
                    Exact Date
                  </ToggleButton>
                </ToggleButtonGroup>

                {dateMode === 'relative' ? (
                  <>
                    {renderRelativeDatePicker(
                      operator === 'between' ? 'From' : 'Date',
                      relativeDateFrom,
                      setRelativeDateFrom,
                      `${field.name} from`,
                    )}
                    {operator === 'between' && renderRelativeDatePicker(
                      'To',
                      relativeDateTo,
                      setRelativeDateTo,
                      `${field.name} to`,
                    )}
                  </>
                ) : (
                  <>
                    {renderAbsoluteDatePicker(
                      operator === 'between' ? 'From' : 'Date',
                      dateFrom,
                      setDateFrom,
                      `${field.name} from date`,
                    )}
                    {operator === 'between' && renderAbsoluteDatePicker(
                      'To',
                      dateTo,
                      setDateTo,
                      `${field.name} to date`,
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.v4.border}` }}>
        <Button
          variant="contained"
          onClick={handleApply}
          fullWidth
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.v4.accent,
            '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
          }}
        >
          Apply Filter
        </Button>
      </Box>
    </Box>
  );
};
